/**
 * Phase 14 Plan 14-02 — ApproverApproveDialog
 *
 * Dialog for the Approver to approve a single Checked entry.
 * Remarks are optional (max 500 chars). Uses react-hook-form + Zod via useZodForm.
 */

import type { JSX } from 'react';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useApproverApproveMutation } from '@/hooks/useLogsheetApproverQueries';
import { useZodForm } from '@/lib/forms/useZodForm';
import { toast } from '@/lib/toast';

const approveSchema = z.object({
  remarks: z.string().max(500).optional(),
});

export interface ApproverApproveDialogProps {
  readonly open: boolean;
  readonly entryId: number;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export function ApproverApproveDialog({
  open,
  entryId,
  onClose,
  onSuccess,
}: ApproverApproveDialogProps): JSX.Element {
  const form = useZodForm({
    schema: approveSchema,
    defaultValues: { remarks: '' },
  });

  const mutation = useApproverApproveMutation();

  function handleClose(): void {
    form.reset();
    mutation.reset();
    onClose();
  }

  const onSubmit = form.handleSubmit((values) => {
    const remarks = values.remarks?.trim();
    const body = remarks !== undefined && remarks !== '' ? { remarks } : {};
    mutation.mutate(
      { id: entryId, body },
      {
        onSuccess: () => {
          toast.success('Entry approved successfully');
          form.reset();
          onSuccess();
        },
        onError: (err) => {
          toast.error('Could not approve entry', {
            description: err.detail ?? err.message,
          });
        },
      },
    );
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
    >
      <DialogContent data-testid="approver-approve-dialog">
        <DialogHeader>
          <DialogTitle>Approve Entry #{entryId}</DialogTitle>
          <DialogDescription>
            Approve this logsheet entry. It will move to Approved status and become immutable. Add
            optional remarks for reference.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} noValidate>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label htmlFor="approver-approve-remarks">Remarks (optional)</Label>
              <Textarea
                id="approver-approve-remarks"
                placeholder="Optional approval remarks…"
                rows={3}
                data-testid="approver-approve-dialog-remarks"
                {...form.register('remarks')}
              />
              {form.formState.errors.remarks !== undefined && (
                <p className="text-xs text-destructive">{form.formState.errors.remarks.message}</p>
              )}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={mutation.isPending}
              data-testid="approver-approve-dialog-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending}
              data-testid="approver-approve-dialog-submit"
            >
              {mutation.isPending ? 'Approving…' : 'Approve'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
